/**
 * Error sanitization utility for client-side error handling.
 * Maps database and API errors to user-friendly messages without exposing schema details.
 */

// PostgreSQL error codes mapped to user-friendly messages
const POSTGRES_ERROR_MAP: Record<string, string> = {
  // Constraint violations
  "23505": "This item already exists",
  "23503": "Cannot perform this action - related data exists",
  "23502": "Required information is missing",
  "23514": "The provided value is not allowed",
  // Permission errors
  "42501": "You don't have permission to perform this action",
  "42503": "Access denied",
  // Connection/transaction errors
  "08000": "Connection issue. Please try again",
  "08003": "Connection issue. Please try again",
  "40001": "Please try again - concurrent update detected",
  "40P01": "Please try again - system was busy",
};

// PostgREST error patterns
const POSTGREST_PATTERNS: Array<{ pattern: RegExp; message: string }> = [
  { pattern: /violates row-level security/i, message: "You don't have permission to access this data" },
  { pattern: /JWT expired/i, message: "Your session has expired. Please log in again" },
  { pattern: /invalid token/i, message: "Authentication error. Please log in again" },
  { pattern: /duplicate key/i, message: "This item already exists" },
  { pattern: /foreign key violation/i, message: "Cannot perform this action - related data exists" },
  { pattern: /not found/i, message: "The requested item was not found" },
  { pattern: /already registered/i, message: "This email is already registered" },
  { pattern: /Invalid login credentials/i, message: "Invalid email or password" },
  { pattern: /email not confirmed/i, message: "Please verify your email address" },
  { pattern: /rate limit/i, message: "Too many requests. Please wait a moment" },
];

// Common auth error patterns
const AUTH_ERROR_PATTERNS: Array<{ pattern: RegExp; message: string }> = [
  { pattern: /invalid.*credentials/i, message: "Invalid email or password" },
  { pattern: /user.*not.*found/i, message: "Account not found" },
  { pattern: /password.*incorrect/i, message: "Incorrect password" },
  { pattern: /email.*taken/i, message: "This email is already in use" },
  { pattern: /weak.*password/i, message: "Password is too weak" },
];

/**
 * Sanitizes error messages before displaying to users.
 * Prevents exposure of database schema, table names, and internal details.
 * 
 * @param error - The error object or message from Supabase/database
 * @param fallbackMessage - Optional custom fallback message
 * @returns A user-friendly error message
 */
export function sanitizeError(
  error: unknown,
  fallbackMessage = "An error occurred. Please try again"
): string {
  if (!error) return fallbackMessage;

  // Extract error message and code
  let message: string;
  let code: string | undefined;

  if (typeof error === "string") {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
    // Check for Supabase/PostgreSQL error codes
    code = (error as any).code;
  } else if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>;
    message = (err.message as string) || (err.error_description as string) || (err.error as string) || "";
    code = err.code as string;
  } else {
    return fallbackMessage;
  }

  // Check PostgreSQL error code first
  if (code && POSTGRES_ERROR_MAP[code]) {
    return POSTGRES_ERROR_MAP[code];
  }

  // Check PostgREST patterns
  for (const { pattern, message: userMessage } of POSTGREST_PATTERNS) {
    if (pattern.test(message)) {
      return userMessage;
    }
  }

  // Check auth error patterns
  for (const { pattern, message: userMessage } of AUTH_ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return userMessage;
    }
  }

  // If the message contains potentially sensitive information, use fallback
  const sensitivePatterns = [
    /column/i,
    /table/i,
    /schema/i,
    /relation/i,
    /constraint/i,
    /index/i,
    /trigger/i,
    /function.*\(/i,
    /pgsql/i,
    /postgres/i,
    /sql/i,
    /_id/i,
    /uuid/i,
    /public\./i,
    /auth\./i,
  ];

  for (const pattern of sensitivePatterns) {
    if (pattern.test(message)) {
      // Log the actual error for debugging (server-side in production)
      console.warn("[Security] Sanitized error:", message);
      return fallbackMessage;
    }
  }

  // If the message is short and doesn't contain sensitive info, it might be safe to show
  if (message.length < 100 && message.length > 0) {
    return message;
  }

  return fallbackMessage;
}

/**
 * Creates a toast-friendly error handler that sanitizes errors.
 * Use with toast.error(sanitizeError(error)) pattern.
 */
export function createSafeErrorHandler(
  toastFn: (message: string) => void,
  fallbackMessage?: string
) {
  return (error: unknown) => {
    toastFn(sanitizeError(error, fallbackMessage));
  };
}
